import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowUpRight, Clock } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { CtaBanner } from "@/components/CtaBanner";
import { IMAGES } from "@/data/content";

import { API } from "@/lib/api";

export const formatDate = (iso) => {
  try {
    const date = new Date(iso + "T00:00:00");
    return Number.isNaN(date.getTime()) ? "" : date.toLocaleDateString("el-GR", { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return "";
  }
};

const CAT_TONES = ["#C26D52", "#2C4A6F", "#5A7265", "#C5A059", "#6F86A3"];
export const categoryTone = (cat = "") => CAT_TONES[Math.abs([...cat].reduce((a, c) => a + c.charCodeAt(0), 0)) % CAT_TONES.length];
const ART_IMAGES = [IMAGES.commercial, IMAGES.port, IMAGES.technology, IMAGES.planning, IMAGES.production, IMAGES.warehouse];
const ORIGINAL_IMAGES = {
  "systima-poliseon-mikri-epixeirisi": ART_IMAGES[0],
  "eksagwges-pente-vimata-nea-agora": ART_IMAGES[1],
  "ai-stin-epixeirisi-pragmatiki-aksia": ART_IMAGES[2],
  "stratigiki-se-efarmogi": ART_IMAGES[3],
};
const articleImage = (article, fallback) => article.image || ORIGINAL_IMAGES[article.slug] || fallback;

export default function Insights() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/insights/articles`)
      .then((res) => setArticles(res.data))
      .catch(() => setArticles([]))
      .finally(() => setLoading(false));
  }, []);

  const [lead, ...rest] = articles;

  return (
    <div data-testid="insights-page">
      <PageHero
        testId="insights-hero"
        eyebrow="Άρθρα & Οπτικές"
        tone="#C5A059"
        title={
          <>
            Πρακτικές ιδέες για επιχειρήσεις που <span className="italic text-[#C26D52]">θέλουν να προχωρήσουν.</span>
          </>
        }
        desc="Σύντομα, ουσιαστικά κείμενα από την οπτική της CRESTIA: πωλήσεις, οργάνωση, εξαγωγές, AI και εφαρμογή στρατηγικής — χωρίς θεωρία για τη θεωρία."
      />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24 lg:pb-32">
        {loading ? (
          <p className="text-sm text-[#6B7A8F]" data-testid="insights-loading">Φόρτωση άρθρων...</p>
        ) : articles.length === 0 ? (
          <p className="text-sm text-[#6B7A8F]" data-testid="insights-empty">Σύντομα κοντά σας με νέο περιεχόμενο.</p>
        ) : (
          <div className="space-y-6">
            <Reveal>
              <Link to={`/insights/${lead.slug}`} data-testid={`insight-card-${lead.slug}`} className="group grid grid-cols-1 lg:grid-cols-12 overflow-hidden rounded-[32px] bg-white border border-[#0E1B31]/[0.07] surface-hover">
                <div className="lg:col-span-6 relative h-[280px] lg:h-auto overflow-hidden">
                  <img src={articleImage(lead, ART_IMAGES[0])} alt="" className="absolute inset-0 h-full w-full object-cover transition-transform duration-1000 group-hover:scale-105" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-tr from-[#0E1B31]/40 to-transparent" />
                  <span className="absolute left-6 top-6 rounded-full glass px-3 py-1.5 font-mono-brand text-[10px] tracking-[0.25em] uppercase" style={{ color: categoryTone(lead.category) }}>{lead.category}</span>
                </div>
                <div className="lg:col-span-6 p-8 lg:p-12 flex flex-col">
                  <p className="text-[12px] text-[#6B7A8F] flex items-center gap-3">
                    <span>{formatDate(lead.date)}</span>
                    <span className="h-1 w-1 rounded-full bg-[#C5A059]" />
                    <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {lead.reading_time}</span>
                  </p>
                  <h2 className="mt-5 font-display text-3xl lg:text-4xl font-semibold text-[#0E1B31] leading-[1.15]">{lead.title}</h2>
                  <p className="mt-5 text-base leading-relaxed text-[#3B4A5E] flex-1">{lead.excerpt}</p>
                  <span className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-[#C26D52]">
                    Διαβάστε το άρθρο <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </span>
                </div>
              </Link>
            </Reveal>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {rest.map((a, i) => {
                const tone = categoryTone(a.category);
                return (
                  <Reveal key={a.slug} delay={i * 0.06}>
                    <Link to={`/insights/${a.slug}`} data-testid={`insight-card-${a.slug}`} className="group flex flex-col h-full overflow-hidden rounded-[28px] bg-white border border-[#0E1B31]/[0.07] surface-hover">
                      <div className="relative h-44 overflow-hidden">
                        <img src={articleImage(a, ART_IMAGES[(i + 1) % ART_IMAGES.length])} alt="" className="absolute inset-0 h-full w-full object-cover transition-transform duration-1000 group-hover:scale-105" loading="lazy" />
                        <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, transparent 40%, ${tone}CC 100%)` }} />
                        <span className="absolute left-5 bottom-4 font-mono-brand text-[10px] tracking-[0.25em] uppercase text-white">{a.category}</span>
                      </div>
                      <div className="p-7 flex flex-col flex-1">
                        <p className="text-[11px] text-[#6B7A8F]">{formatDate(a.date)} · {a.reading_time}</p>
                        <h3 className="mt-3 font-display text-2xl font-semibold text-[#0E1B31] leading-snug">{a.title}</h3>
                        <p className="mt-3 text-sm leading-relaxed text-[#3B4A5E] flex-1">{a.excerpt}</p>
                        <span className="mt-5 inline-flex items-center gap-2 text-xs font-semibold" style={{ color: tone }}>
                          Διαβάστε το άρθρο <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </span>
                      </div>
                    </Link>
                  </Reveal>
                );
              })}
            </div>
          </div>
        )}
      </section>

      <CtaBanner
        title="Θέλετε να τα εφαρμόσετε όλα αυτά στη δική σας επιχείρηση;"
        subtitle="Τα άρθρα δίνουν τη λογική — η συνεργασία δίνει το πλάνο, τα συστήματα και την εφαρμογή."
        buttonLabel="Ας μιλήσουμε για την επιχείρησή σας"
      />
    </div>
  );
}
